import { Box, Divider, IconButton, List, Paper, Tooltip } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeftOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRightOutlined";
import type { NavGroup, NavItem } from "@/types/navigation";
import NavRow from "@/components/ProjectPage/NavRow";
import SearchBox from "@/components/ui/SearchBox";
import { ProjectPanelRoute } from "@/types/routes";

const selectedBgSx = { bgcolor: "action.selected" } as const;

const SPECIAL_ITEM_KEYS = new Set<NavItem["key"]>([ProjectPanelRoute.Settings]);

const getNavItemByKey = (navGroups: readonly NavGroup[], key: NavItem["key"]): NavItem | null => {
  for (const group of navGroups) {
    for (const item of group.items) {
      if (item.key === key) {
        return item;
      }
    }
  }

  return null;
};

const renderNavGroups = ({
  navGroups,
  isCollapsed,
  disableSelection,
  activeKey,
  onSelect
}: {
  readonly navGroups: readonly NavGroup[];
  readonly isCollapsed: boolean;
  readonly disableSelection: boolean;
  readonly activeKey: string;
  readonly onSelect: (key: string) => void;
}) => {
  const filteredGroups = navGroups
    .map((g) => ({ ...g, items: g.items.filter((it) => !SPECIAL_ITEM_KEYS.has(it.key)) }))
    .filter((g) => g.items.length > 0);

  return filteredGroups.map((group) => (
    <Box key={group.id}>
      <List dense disablePadding sx={{ px: 1, py: 1 }}>
        {group.items.map((item) => (
          <Box
            key={item.key}
            sx={{
              display: "flex",
              justifyContent: isCollapsed ? "center" : "stretch"
            }}
          >
            <NavRow item={item} isCollapsed={isCollapsed} isActive={disableSelection ? false : item.key === activeKey} onSelect={onSelect} />
          </Box>
        ))}
      </List>
      <Divider />
    </Box>
  ));
};

const renderSystemSettings = ({
  navGroups,
  isCollapsed,
  disableSelection,
  activeKey,
  onSelect
}: {
  readonly navGroups: readonly NavGroup[];
  readonly isCollapsed: boolean;
  readonly disableSelection: boolean;
  readonly activeKey: string;
  readonly onSelect: (key: string) => void;
}) => {
  const systemSettingsItem = getNavItemByKey(navGroups, ProjectPanelRoute.Settings);
  const Icon = systemSettingsItem?.icon;
  if (!systemSettingsItem || !Icon) {
    return null;
  }

  const isActive = !disableSelection && activeKey === systemSettingsItem.key;
  const isDisabled = disableSelection || systemSettingsItem.disabled === true;

  if (isCollapsed) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 1 }}>
        <Tooltip title={systemSettingsItem.label} placement="right">
          <span style={{ display: "inline-flex" }}>
            <IconButton
              {...(isDisabled ? { disabled: true } : { onClick: () => onSelect(systemSettingsItem.key) })}
              sx={{
                borderRadius: 1,
                ...(isDisabled ? { opacity: 0.45 } : null),
                ...(isActive ? selectedBgSx : null)
              }}
            >
              <Icon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    );
  }

  return (
    <List>
      <NavRow
        item={systemSettingsItem}
        isCollapsed={false}
        isActive={disableSelection ? false : activeKey === systemSettingsItem.key}
        onSelect={onSelect}
      />
    </List>
  );
};

interface LeftSidebarProps {
  readonly headerHeight: number;
  readonly width: number;
  readonly isCollapsed: boolean;
  readonly onToggleCollapsed: () => void;
  readonly navGroups: readonly NavGroup[];
  readonly activeKey: string;
  readonly disableSelection?: boolean;
  readonly onSelect: (key: string) => void;
}

const LeftSidebar = ({
  headerHeight,
  width,
  isCollapsed,
  onToggleCollapsed,
  navGroups,
  activeKey,
  disableSelection = false,
  onSelect
}: LeftSidebarProps) => (
  <Paper
    elevation={0}
    sx={{
      position: "sticky",
      top: headerHeight,
      height: `calc(100vh - ${headerHeight}px)`,
      width,
      borderRightWidth: 1,
      borderRightStyle: "solid",
      transition: (t) => t.transitions.create("width", { duration: t.transitions.duration.shortest }),
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      alignItems: isCollapsed ? "center" : "stretch"
    }}
  >
    <Box sx={{ width: "100%", px: 1, pt: 2 }}>
      {isCollapsed ? (
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <IconButton size="small" onClick={onToggleCollapsed}>
            <ChevronRightIcon />
          </IconButton>
        </Box>
      ) : (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <SearchBox
              placeholder="Search"
              height={32}
              sx={{
                "& .MuiInputBase-input": {
                  fontSize: 13,
                  fontWeight: 500,
                  letterSpacing: "0.46px"
                }
              }}
            />
          </Box>
          <IconButton size="small" onClick={onToggleCollapsed}>
            <ChevronLeftIcon />
          </IconButton>
        </Box>
      )}
    </Box>
    <Box sx={{ flex: 1, width: "100%", py: 2, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Box sx={{ flex: 1, minHeight: 0, overflow: "auto" }}>{renderNavGroups({ navGroups, isCollapsed, disableSelection, activeKey, onSelect })}</Box>
      {/* systemSettings pinned at the very bottom */}
      <Box sx={{ mt: "auto" }}>{renderSystemSettings({ navGroups, isCollapsed, disableSelection, activeKey, onSelect })}</Box>
    </Box>
  </Paper>
);

export default LeftSidebar;
